import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  X,
  Settings,
  Compass,
  UserCheck,
  Target,
  MessageCircle,
  ClipboardList,
  Info,
  Users,
  LogOut,
  ChevronRight,
  Fingerprint,
  Sparkles,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { base44 } from "@/api/base44Client";
import { getMembershipConfig } from "@/lib/membershipUtils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const KG_PER_LB = 0.45359237;

/**
 * Premium Full-Screen Athlete Profile for TA Fitness Mobile App
 * Replaces the legacy small dropdown with a personalized athlete command deck.
 */
export default function AthleteProfileModal({ open, onClose }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("my_space");
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  // Membership configuration
  const cfg = getMembershipConfig(user?.membership_type);

  // User initials for fallback avatar
  const initials = user?.full_name
    ? user.full_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : (user?.email?.[0] || "?").toUpperCase();

  // Reset tab to "my_space" each time profile opens
  useEffect(() => {
    if (open) {
      setActiveTab("my_space");
    }
  }, [open]);

  // Lock background scroll when modal is open and handle Escape key
  useEffect(() => {
    if (!open) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  // Query trainers list to identify trainer role / assigned trainer
  const { data: trainers = [] } = useQuery({
    queryKey: ["trainers"],
    queryFn: () => base44.entities.Trainer.list(),
    enabled: open && !!user,
  });

  const isTrainer =
    user?.role === "admin" ||
    user?.role === "trainer" ||
    trainers.some((t) => t.user_id === user?.id && t.is_active);

  // Query fitness goals to show active goal count
  const { data: goals = [] } = useQuery({
    queryKey: ["fitness_goals", user?.id],
    queryFn: () =>
      base44.entities.FitnessGoal.filter({ is_active: true }, "-created_date", 100),
    enabled: open && !!user,
  });

  // Query progress logs to read latest recorded weight
  const { data: logs = [] } = useQuery({
    queryKey: ["progress"],
    queryFn: () => base44.entities.ProgressLog.list("-date", 30),
    enabled: open && !!user,
  });

  // Extract latest weight from progress log or user profile
  const weightLogs = useMemo(() => {
    return [...logs]
      .filter((l) => typeof l.weight_kg === "number" && !Number.isNaN(l.weight_kg))
      .sort((a, b) => {
        const dateCmp = (b.date || "").localeCompare(a.date || "");
        if (dateCmp !== 0) return dateCmp;
        return (b.created_date || "").localeCompare(a.created_date || "");
      });
  }, [logs]);

  const latestLog = weightLogs[0];
  const useLbs = user?.use_lbs ?? true;
  const useInches = user?.use_inches ?? true;

  let weightDisplay = "—";
  if (latestLog?.weight_kg) {
    const w = useLbs ? latestLog.weight_kg / KG_PER_LB : latestLog.weight_kg;
    weightDisplay = `${(Math.round(w * 10) / 10).toFixed(1)} ${useLbs ? "lbs" : "kg"}`;
  } else if (user?.current_weight) {
    weightDisplay = `${user.current_weight} ${useLbs ? "lbs" : "kg"}`;
  }

  // Format height with user preferred units
  let heightDisplay = "—";
  if (user?.height) {
    if (useInches) {
      const feet = Math.floor(user.height / 12);
      const inches = user.height % 12;
      heightDisplay = `${feet}'${inches}"`;
    } else {
      heightDisplay = `${user.height} cm`;
    }
  }

  // Format age
  const ageDisplay = user?.age ? `${user.age}` : "—";

  const handleNavigate = (path) => {
    onClose();
    navigate(path);
  };

  const handleSignOutConfirm = () => {
    setConfirmSignOut(false);
    onClose();
    logout(true);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-[#030A17] text-white flex flex-col overflow-hidden animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      data-testid="athlete-profile-overlay"
    >
      {/* Background ambient lighting */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 90% 45% at 50% -10%, rgba(14, 165, 233, 0.15), transparent 75%), #030A17",
        }}
      />

      {/* Top Header */}
      <header
        className="relative z-10 shrink-0 border-b border-white/5 bg-[#030A17]/80 backdrop-blur-md"
        style={{
          paddingTop: "env(safe-area-inset-top)",
        }}
      >
        <div className="h-14 px-4 flex items-center justify-between max-w-xl mx-auto w-full">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close Profile"
            data-testid="athlete-profile-close-btn"
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 active:scale-95 transition flex items-center justify-center cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>

          <span className="text-[11px] font-black uppercase tracking-[0.25em] text-sky-400">
            MY TA FITNESS
          </span>

          <button
            type="button"
            onClick={() => handleNavigate("/account")}
            aria-label="Open Settings"
            data-testid="athlete-profile-settings-btn"
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 active:scale-95 transition flex items-center justify-center cursor-pointer"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Scrollable Main Experience */}
      <div className="relative z-10 flex-1 overflow-y-auto px-4 py-5 space-y-6 max-w-xl mx-auto w-full pb-[calc(2.5rem+env(safe-area-inset-bottom))]">
        {/* Section 3: Athlete Identity Header */}
        <div className="flex flex-col items-center text-center pt-1" data-testid="athlete-identity-header">
          <div className="relative mb-3.5">
            {user?.picture_url ? (
              <img
                src={user.picture_url}
                alt={user?.full_name || "Profile"}
                className="w-22 h-22 sm:w-24 sm:h-24 rounded-full object-cover ring-2 ring-sky-400/50 shadow-[0_0_24px_rgba(56,189,248,0.25)]"
              />
            ) : (
              <div className="w-22 h-22 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-sky-500/20 to-blue-600/30 border-2 border-sky-400/50 flex items-center justify-center text-2xl font-black text-sky-300 ring-2 ring-sky-400/30 shadow-[0_0_24px_rgba(56,189,248,0.25)]">
                {initials}
              </div>
            )}
            {cfg && (
              <span
                className={`absolute bottom-1 right-1 w-4 h-4 rounded-full ${cfg.dotColor} border-2 border-[#030A17]`}
                title={cfg.name}
              />
            )}
          </div>

          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-sky-400 mb-1">
            ATHLETE PROFILE
          </span>
          <h2 className="text-2xl sm:text-3xl font-black font-display text-white tracking-tight mb-2">
            {user?.full_name || "TA Athlete"}
          </h2>

          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sky-500/10 border border-sky-400/30 text-sky-300 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-sky-400" />
            <span>{cfg?.name || cfg?.label || "TA Member"}</span>
          </div>
        </div>

        {/* Section 4: Your Fitness Identity */}
        <div className="rounded-2xl p-4 bg-[#0A1324] border border-white/10 shadow-inner" data-testid="fitness-identity-section">
          <div className="flex items-center justify-center gap-1.5 mb-3.5">
            <Fingerprint className="w-3.5 h-3.5 text-sky-400" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-sky-400">
              YOUR FITNESS IDENTITY
            </span>
          </div>

          <div className="grid grid-cols-3 divide-x divide-white/10 text-center">
            <div className="px-2">
              <p className="text-lg sm:text-xl font-black font-display text-white tracking-tight">
                {weightDisplay}
              </p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">
                WEIGHT
              </p>
            </div>
            <div className="px-2">
              <p className="text-lg sm:text-xl font-black font-display text-white tracking-tight">
                {heightDisplay}
              </p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">
                HEIGHT
              </p>
            </div>
            <div className="px-2">
              <p className="text-lg sm:text-xl font-black font-display text-white tracking-tight">
                {ageDisplay}
              </p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">
                AGE
              </p>
            </div>
          </div>
        </div>

        {/* Section 5: Custom Three-Column Navigation */}
        <div
          className="grid gap-2 w-full"
          style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
          role="tablist"
          aria-label="Athlete Profile Navigation"
          data-testid="athlete-profile-tabs"
        >
          {/* Tile 1: MY SPACE */}
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "my_space"}
            onClick={() => setActiveTab("my_space")}
            data-testid="athlete-tab-my-space"
            className={`w-full rounded-2xl py-3 px-1.5 flex flex-col items-center justify-center gap-1.5 transition-all border cursor-pointer ${
              activeTab === "my_space"
                ? "bg-[#0E203C] border-sky-400/60 shadow-[0_0_15px_rgba(56,189,248,0.2)]"
                : "bg-[#0B1528] border-white/10 hover:border-white/20 text-slate-400"
            }`}
          >
            <Compass
              className={`w-5 h-5 shrink-0 transition-colors ${
                activeTab === "my_space" ? "text-sky-400" : "text-slate-400"
              }`}
            />
            <span
              className={`text-[11px] sm:text-xs font-bold uppercase tracking-wider text-center truncate w-full transition-colors ${
                activeTab === "my_space" ? "text-white" : "text-slate-400"
              }`}
            >
              MY SPACE
            </span>
            <span
              className={`w-8 h-0.5 rounded-full transition-all shrink-0 ${
                activeTab === "my_space"
                  ? "bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.8)]"
                  : "bg-transparent"
              }`}
            />
          </button>

          {/* Tile 2: COACHING */}
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "coaching"}
            onClick={() => setActiveTab("coaching")}
            data-testid="athlete-tab-coaching"
            className={`w-full rounded-2xl py-3 px-1.5 flex flex-col items-center justify-center gap-1.5 transition-all border cursor-pointer ${
              activeTab === "coaching"
                ? "bg-[#0E203C] border-sky-400/60 shadow-[0_0_15px_rgba(56,189,248,0.2)]"
                : "bg-[#0B1528] border-white/10 hover:border-white/20 text-slate-400"
            }`}
          >
            <UserCheck
              className={`w-5 h-5 shrink-0 transition-colors ${
                activeTab === "coaching" ? "text-sky-400" : "text-slate-400"
              }`}
            />
            <span
              className={`text-[11px] sm:text-xs font-bold uppercase tracking-wider text-center truncate w-full transition-colors ${
                activeTab === "coaching" ? "text-white" : "text-slate-400"
              }`}
            >
              COACHING
            </span>
            <span
              className={`w-8 h-0.5 rounded-full transition-all shrink-0 ${
                activeTab === "coaching"
                  ? "bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.8)]"
                  : "bg-transparent"
              }`}
            />
          </button>

          {/* Tile 3: ACCOUNT */}
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "account"}
            onClick={() => setActiveTab("account")}
            data-testid="athlete-tab-account"
            className={`w-full rounded-2xl py-3 px-1.5 flex flex-col items-center justify-center gap-1.5 transition-all border cursor-pointer ${
              activeTab === "account"
                ? "bg-[#0E203C] border-sky-400/60 shadow-[0_0_15px_rgba(56,189,248,0.2)]"
                : "bg-[#0B1528] border-white/10 hover:border-white/20 text-slate-400"
            }`}
          >
            <Settings
              className={`w-5 h-5 shrink-0 transition-colors ${
                activeTab === "account" ? "text-sky-400" : "text-slate-400"
              }`}
            />
            <span
              className={`text-[11px] sm:text-xs font-bold uppercase tracking-wider text-center truncate w-full transition-colors ${
                activeTab === "account" ? "text-white" : "text-slate-400"
              }`}
            >
              ACCOUNT
            </span>
            <span
              className={`w-8 h-0.5 rounded-full transition-all shrink-0 ${
                activeTab === "account"
                  ? "bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.8)]"
                  : "bg-transparent"
              }`}
            />
          </button>
        </div>

        {/* Tab Content Display */}
        <div className="pt-1">
          {/* TAB 1: MY SPACE */}
          {activeTab === "my_space" && (
            <div className="space-y-4 animate-in fade-in duration-150" data-testid="athlete-content-my-space">
              {/* Featured My Goals Card */}
              <button
                type="button"
                onClick={() => handleNavigate("/my-goals")}
                className="w-full text-left p-4 rounded-2xl bg-gradient-to-r from-sky-500/15 via-sky-500/10 to-blue-600/10 border border-sky-500/30 hover:border-sky-400/60 shadow-[0_0_20px_rgba(56,189,248,0.1)] active:scale-[0.99] transition-all flex items-center justify-between group cursor-pointer"
                data-testid="athlete-featured-goals-card"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-12 h-12 rounded-xl bg-sky-500/20 border border-sky-400/40 text-sky-300 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform shadow-[0_0_12px_rgba(56,189,248,0.25)]">
                    <Target className="w-6 h-6 text-sky-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-base text-white">My Goals</h3>
                      {goals.length > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-300 border border-sky-400/30">
                          {goals.length} ACTIVE
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-300 mt-0.5">
                      Your next milestone starts here.
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-sky-400/70 group-hover:text-sky-300 group-hover:translate-x-0.5 transition-all shrink-0" />
              </button>

              {/* Quick Access Grid (2 columns) */}
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2.5 px-1">
                  QUICK ACCESS
                </p>
                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={() => handleNavigate("/my-trainer")}
                    className="p-3.5 rounded-2xl bg-[#0B1528] border border-white/10 hover:border-sky-500/40 active:scale-95 transition-all text-left flex items-center justify-between group cursor-pointer"
                    data-testid="athlete-shortcut-my-trainer"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-sky-500/15 border border-sky-400/30 text-sky-400 flex items-center justify-center shrink-0">
                        <UserCheck className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-bold text-white group-hover:text-sky-300 transition-colors">
                        My Trainer
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-sky-400 group-hover:translate-x-0.5 transition-all shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => handleNavigate("/chat")}
                    className="p-3.5 rounded-2xl bg-[#0B1528] border border-white/10 hover:border-sky-500/40 active:scale-95 transition-all text-left flex items-center justify-between group cursor-pointer"
                    data-testid="athlete-shortcut-chat"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-sky-500/15 border border-sky-400/30 text-sky-400 flex items-center justify-center shrink-0">
                        <MessageCircle className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-bold text-white group-hover:text-sky-300 transition-colors">
                        Chat
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-sky-400 group-hover:translate-x-0.5 transition-all shrink-0" />
                  </button>

                  {isTrainer && (
                    <button
                      type="button"
                      onClick={() => handleNavigate("/trainer-clients")}
                      className="p-3.5 rounded-2xl bg-[#0B1528] border border-white/10 hover:border-sky-500/40 active:scale-95 transition-all text-left flex items-center justify-between group cursor-pointer"
                      data-testid="athlete-shortcut-my-clients"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-sky-500/15 border border-sky-400/30 text-sky-400 flex items-center justify-center shrink-0">
                          <ClipboardList className="w-4 h-4" />
                        </div>
                        <span className="text-xs font-bold text-white group-hover:text-sky-300 transition-colors">
                          My Clients
                        </span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-sky-400 group-hover:translate-x-0.5 transition-all shrink-0" />
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => handleNavigate("/about")}
                    className="p-3.5 rounded-2xl bg-[#0B1528] border border-white/10 hover:border-sky-500/40 active:scale-95 transition-all text-left flex items-center justify-between group cursor-pointer"
                    data-testid="athlete-shortcut-about"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-sky-500/15 border border-sky-400/30 text-sky-400 flex items-center justify-center shrink-0">
                        <Info className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-bold text-white group-hover:text-sky-300 transition-colors">
                        About Us
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-sky-400 group-hover:translate-x-0.5 transition-all shrink-0" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: COACHING */}
          {activeTab === "coaching" && (
            <div className="space-y-3 animate-in fade-in duration-150" data-testid="athlete-content-coaching">
              <div className="px-1 mb-1">
                <h3 className="text-sm font-bold text-white">Your Coaching Space</h3>
                <p className="text-xs text-slate-400">
                  Personalized training and client communication
                </p>
              </div>

              {/* My Trainer */}
              <button
                type="button"
                onClick={() => handleNavigate("/my-trainer")}
                className="w-full p-4 rounded-2xl bg-[#0B1528] border border-white/10 hover:border-sky-500/40 active:scale-[0.99] transition-all text-left flex items-center justify-between group cursor-pointer"
                data-testid="athlete-coaching-my-trainer"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-400/30 text-sky-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <UserCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white group-hover:text-sky-300 transition-colors">
                      My Trainer
                    </h4>
                    <p className="text-xs text-slate-400">Your personal coaching</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-sky-400 group-hover:translate-x-0.5 transition-all shrink-0" />
              </button>

              {/* Chat */}
              <button
                type="button"
                onClick={() => handleNavigate("/chat")}
                className="w-full p-4 rounded-2xl bg-[#0B1528] border border-white/10 hover:border-sky-500/40 active:scale-[0.99] transition-all text-left flex items-center justify-between group cursor-pointer"
                data-testid="athlete-coaching-chat"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-400/30 text-sky-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <MessageCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white group-hover:text-sky-300 transition-colors">
                      Chat
                    </h4>
                    <p className="text-xs text-slate-400">Messages and conversations</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-sky-400 group-hover:translate-x-0.5 transition-all shrink-0" />
              </button>

              {/* My Clients (Trainer Only) */}
              {isTrainer && (
                <button
                  type="button"
                  onClick={() => handleNavigate("/trainer-clients")}
                  className="w-full p-4 rounded-2xl bg-[#0B1528] border border-white/10 hover:border-sky-500/40 active:scale-[0.99] transition-all text-left flex items-center justify-between group cursor-pointer"
                  data-testid="athlete-coaching-my-clients"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-400/30 text-sky-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <ClipboardList className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-white group-hover:text-sky-300 transition-colors">
                        My Clients
                      </h4>
                      <p className="text-xs text-slate-400">Manage your clients</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-sky-400 group-hover:translate-x-0.5 transition-all shrink-0" />
                </button>
              )}
            </div>
          )}

          {/* TAB 3: ACCOUNT */}
          {activeTab === "account" && (
            <div className="space-y-3 animate-in fade-in duration-150" data-testid="athlete-content-account">
              {/* My Account */}
              <button
                type="button"
                onClick={() => handleNavigate("/account")}
                className="w-full p-4 rounded-2xl bg-[#0B1528] border border-white/10 hover:border-sky-500/40 active:scale-[0.99] transition-all text-left flex items-center justify-between group cursor-pointer"
                data-testid="athlete-account-my-account"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-400/30 text-sky-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <Settings className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white group-hover:text-sky-300 transition-colors">
                      My Account
                    </h4>
                    <p className="text-xs text-slate-400">
                      Profile details, preferences & billing
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-sky-400 group-hover:translate-x-0.5 transition-all shrink-0" />
              </button>

              {/* About Us */}
              <button
                type="button"
                onClick={() => handleNavigate("/about")}
                className="w-full p-4 rounded-2xl bg-[#0B1528] border border-white/10 hover:border-sky-500/40 active:scale-[0.99] transition-all text-left flex items-center justify-between group cursor-pointer"
                data-testid="athlete-account-about"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-400/30 text-sky-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <Info className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white group-hover:text-sky-300 transition-colors">
                      About Us
                    </h4>
                    <p className="text-xs text-slate-400">
                      Learn more about TA Fitness
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-sky-400 group-hover:translate-x-0.5 transition-all shrink-0" />
              </button>

              {/* Admin (Admin role only) */}
              {user?.role === "admin" && (
                <button
                  type="button"
                  onClick={() => handleNavigate("/admin")}
                  className="w-full p-4 rounded-2xl bg-[#0B1528] border border-white/10 hover:border-sky-500/40 active:scale-[0.99] transition-all text-left flex items-center justify-between group cursor-pointer"
                  data-testid="athlete-account-admin"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/15 border border-purple-400/30 text-purple-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <Users className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-white group-hover:text-purple-300 transition-colors">
                        Admin
                      </h4>
                      <p className="text-xs text-slate-400">
                        System administration & user control
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-purple-400 group-hover:translate-x-0.5 transition-all shrink-0" />
                </button>
              )}

              {/* Sign Out */}
              <button
                type="button"
                onClick={() => setConfirmSignOut(true)}
                className="w-full p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/15 hover:border-rose-500/40 active:scale-[0.99] transition-all text-left flex items-center justify-between group cursor-pointer"
                data-testid="athlete-account-sign-out"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-rose-500/20 border border-rose-400/40 text-rose-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <LogOut className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-rose-300 group-hover:text-rose-200 transition-colors">
                      Sign Out
                    </h4>
                    <p className="text-xs text-rose-300/70">
                      Log out of your TA Fitness session
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-rose-400/60 group-hover:text-rose-300 group-hover:translate-x-0.5 transition-all shrink-0" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sign Out Confirmation Alert Dialog */}
      <AlertDialog open={confirmSignOut} onOpenChange={setConfirmSignOut}>
        <AlertDialogContent className="z-[70] bg-[#0A1324] border border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Sign out of TA Fitness?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              You will be logged out of your session and returned to the login screen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setConfirmSignOut(false)}
              className="bg-white/10 border-white/20 text-white hover:bg-white/15"
              data-testid="athlete-sign-out-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSignOutConfirm}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="athlete-sign-out-confirm"
            >
              Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
